import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import LeasingIncomeSchemaEventNegotiationLimitComponent from './LeasingIncomeSchemaEventNegotiationLimitComponent';
import IconButton from 'components/common/buttons/IconButton';
import FlexBox from 'components/common/FlexBox';
import { get, sequence, set } from '@totalsoft/change-tracking-react';
import useQuery from 'hooks/apollo/useQuery';
import { USER_GROUPS_QUERY } from 'features/common/components/userGroup/userGroupList/queries';
import { Typography } from '@totalsoft/rocket-ui';
import { useTranslation } from 'react-i18next';
import LabeledCheckbox from 'components/common/inputs/LabeledCheckbox';
import { useReactiveVar } from '@apollo/client';
import { negotiationLimitsInAbsoluteValuesVar } from '../../variables';

const getEmptyLimit = (schemaId, detailId) => ({
  id: crypto.randomUUID(),
  schemaId,
  detailId,
  lowerPercentLimit: null,
  upperPercentLimit: null,
  lowerValueLimit: null,
  upperValueLimit: null,
  firstApprovalUserGroups: null,
  secondApprovalUserGroups: null
});

const LeasingIncomeSchemaEventNegotiationLimitsComponent = ({
  schemaId,
  detailId,
  lens,
  validation,
  disabled,
  setLimitsInAbsoluteValuesDirty
}) => {
  const { t } = useTranslation();

  const limitsInAbsoluteValues = useReactiveVar(negotiationLimitsInAbsoluteValuesVar);

  // #region Collections

  const { data: userGroupsData } = useQuery(USER_GROUPS_QUERY);

  const userGroups = useMemo(() => userGroupsData?.userGroupsConfig || [], [userGroupsData]);

  // #endregion

  const handleAdd = () => {
    const current = lens |> get;
    const maxOrderNo = Math.max(...current.map(limit => limit.orderNo), 0);
    let newOrderNo = 0;
    for (let i = 1; i < maxOrderNo; i++) {
      if (!current.some(limit => limit.orderNo === i)) {
        newOrderNo = i;
        break;
      }
    }
    if (!newOrderNo) newOrderNo = maxOrderNo + 1;
    const newLimit = getEmptyLimit(schemaId, detailId);
    newLimit.orderNo = newOrderNo;
    (lens |> set)([...current, newLimit]);
  };

  const handleDelete = id => {
    const current = lens |> get;
    (lens |> set)(current.filter(limit => limit.id !== id));
  };

  const hasLimits = (lens.length |> get) > 0;

  return (
    <FlexBox direction='column' gap={1.5} px={1.5} py={1.5}>
      <FlexBox justifyContent='space-between' px={1}>
        <LabeledCheckbox
          label={t('LeasingIncomeSchema.NegotiationLimitsInAbsoluteValues')}
          checked={limitsInAbsoluteValues}
          onChange={value => {
            setLimitsInAbsoluteValuesDirty(true);
            negotiationLimitsInAbsoluteValuesVar(value);
          }}
          disabled={disabled}
        />
        <IconButton type='add' onClick={handleAdd} disabled={disabled} />
      </FlexBox>
      {!hasLimits && (
        <Typography color='textPrimary' align='center'>
          {t('LeasingIncomeSchema.NoNegotiationLimits')}
        </Typography>
      )}
      {hasLimits &&
        (lens |> sequence).map((limitLens, index) => (
          <LeasingIncomeSchemaEventNegotiationLimitComponent
            key={limitLens.id |> get}
            lens={limitLens}
            userGroups={userGroups}
            onDelete={handleDelete}
            validation={validation[index]}
            disabled={disabled}
          />
        ))}
    </FlexBox>
  );
};

LeasingIncomeSchemaEventNegotiationLimitsComponent.propTypes = {
  schemaId: PropTypes.number,
  detailId: PropTypes.number,
  lens: PropTypes.object,
  validation: PropTypes.object,
  disabled: PropTypes.bool,
  setLimitsInAbsoluteValuesDirty: PropTypes.func
};

export default LeasingIncomeSchemaEventNegotiationLimitsComponent;
